import React, { useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import {
  CheckCircle2, AlertCircle, Wand2,
  ChevronUp, ChevronDown, Loader2, Lock, RotateCcw,
} from 'lucide-react';
import DispatchJobCard from './DispatchJobCard';

// ── Initials avatar ────────────────────────────────────────────────────────────

function TechAvatar({ name }) {
  const initials = name
    ? name.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase()
    : '?';

  const colors = [
    'bg-orange-100 text-orange-700',
    'bg-blue-100 text-blue-700',
    'bg-purple-100 text-purple-700',
    'bg-teal-100 text-teal-700',
    'bg-rose-100 text-rose-700',
    'bg-indigo-100 text-indigo-700',
  ];
  const idx = name ? name.charCodeAt(0) % colors.length : 0;

  return (
    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 ${colors[idx]}`}>
      {initials}
    </div>
  );
}

// ── Tech Column ────────────────────────────────────────────────────────────────

const DispatchTechColumn = ({
  techId,
  techName,
  jobs,
  canDrag,
  isAdminView,
  currentUserUid,
  leadsMap,
  onOptimize,   // async () => { savingsMinutes, hasImprovement } — per-tech optimize
  onMoveJob,    // (jobId, direction: 'up'|'down') — up/down reorder
  onReset,      // () => void — open Reset Route sheet (admin/owner only; null otherwise)
  selectedJobIds,  // Set<string> — Sprint 2 bulk-reschedule selection
  onToggleSelect,  // (taskId) => void
}) => {
  const droppableId = `drop-tech-${techId}`;
  const [optimizing, setOptimizing] = useState(false);

  const { setNodeRef, isOver } = useDroppable({
    id: droppableId,
    data: { type: 'tech-column', techId },
  });

  const completed      = jobs.filter((j) => j.status === 'completed').length;
  const remaining      = jobs.length - completed;
  const allDone        = jobs.length > 0 && remaining === 0;
  const sortedIds      = jobs.map((j) => j.id);
  // Only count route jobs for the optimize threshold (appointment jobs are fixed)
  const routeJobCount  = jobs.filter((j) => (j.jobMode || 'route') === 'route').length;

  const handleOptimize = async () => {
    if (!onOptimize || optimizing || jobs.length < 2) return;
    setOptimizing(true);
    try {
      await onOptimize();
    } finally {
      setOptimizing(false);
    }
  };

  return (
    <div
      className={`
        flex flex-col rounded-xl border transition-colors duration-150
        min-w-[260px] max-w-[300px] flex-shrink-0
        ${isOver
          ? 'border-[var(--cb-orange)] bg-[rgba(242,107,29,0.04)] shadow-md'
          : allDone
            ? 'border-emerald-200 bg-emerald-50/30'
            : 'border-gray-200 bg-gray-50/50'
        }
      `}
      data-testid={`dispatch-tech-column-${techId}`}
    >
      {/* ── Column header ─────────────────────────────────────────────────── */}
      <div className="px-3 py-2.5 border-b border-gray-200 bg-white rounded-t-xl">
        <div className="flex items-center gap-2">
          <TechAvatar name={techName} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">{techName}</p>
            <p className="text-[10px] text-gray-400 leading-tight">
              {jobs.length} job{jobs.length !== 1 ? 's' : ''}
            </p>
          </div>

          {/* Optimize Route button — admin only, ≥2 route jobs */}
          {isAdminView && routeJobCount >= 2 && onOptimize && (
            <button
              type="button"
              onClick={handleOptimize}
              disabled={optimizing}
              className="inline-flex items-center gap-1 h-6 px-2 text-[10px] font-semibold rounded-md border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
              data-testid={`optimize-route-btn-${techId}`}
              title={`Optimize ${techName}'s route`}
            >
              {optimizing
                ? <Loader2 className="h-2.5 w-2.5 animate-spin" />
                : <Wand2 className="h-2.5 w-2.5" />
              }
              {optimizing ? 'Optimizing…' : 'Optimize'}
            </button>
          )}

          {/* Reset Route button — owner/admin only, route jobs present */}
          {onReset && routeJobCount > 0 && (
            <button
              type="button"
              onClick={onReset}
              className="inline-flex items-center gap-1 h-6 px-2 text-[10px] font-semibold rounded-md border border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 transition-colors flex-shrink-0"
              data-testid={`reset-route-btn-${techId}`}
              title={`Return ${techName}'s assigned route to Unassigned`}
            >
              <RotateCcw className="h-2.5 w-2.5" />
              Reset
            </button>
          )}

          {/* Status chip */}
          {allDone ? (
            <span className="flex items-center gap-1 text-[10px] font-medium text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full flex-shrink-0">
              <CheckCircle2 className="h-3 w-3" />
              Done
            </span>
          ) : remaining > 0 && !isAdminView ? (
            <span className="flex items-center gap-1 text-[10px] font-medium text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full flex-shrink-0">
              <AlertCircle className="h-3 w-3" />
              {remaining} left
            </span>
          ) : null}
        </div>

        {/* Progress bar */}
        {jobs.length > 0 && (
          <div className="mt-2 h-1 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-1 bg-emerald-500 rounded-full transition-all duration-300"
              style={{ width: `${(completed / jobs.length) * 100}%` }}
            />
          </div>
        )}
      </div>

      {/* ── Job list ──────────────────────────────────────────────────────── */}
      <div
        ref={setNodeRef}
        className="flex-1 p-2 space-y-2 min-h-[80px] overflow-y-auto max-h-[calc(100vh-260px)]"
        data-testid={`dispatch-tech-jobs-${techId}`}
      >
        {isOver && jobs.length === 0 && (
          <div className="border-2 border-dashed border-[var(--cb-orange)] rounded-lg p-4 text-center text-[11px] text-[var(--cb-orange)]">
            Drop here to assign
          </div>
        )}

        <SortableContext items={sortedIds} strategy={verticalListSortingStrategy}>
          {jobs.map((job, idx) => {
            const canChangeStatus = isAdminView || job.assignedTo === currentUserUid;
            const isAppointment   = (job.jobMode || 'route') === 'appointment';
            // Blocked when the adjacent job is a fixed appointment
            const isBlockedUp     = !isAppointment && idx > 0
              && (jobs[idx - 1].jobMode || 'route') === 'appointment';
            const isBlockedDown   = !isAppointment && idx < jobs.length - 1
              && (jobs[idx + 1].jobMode || 'route') === 'appointment';

            return (
              <div key={job.id} className="flex items-start gap-1">
                {/* Reorder controls — admin only, column has >1 job */}
                {isAdminView && onMoveJob && jobs.length > 1 && (
                  <div className="flex flex-col items-center gap-0.5 shrink-0 mt-1.5 w-4">
                    {isAppointment ? (
                      /* Fixed appointment — show lock, no arrows */
                      <div
                        className="w-4 h-8 flex items-center justify-center"
                        title="Fixed appointment — cannot be reordered"
                        data-testid={`dispatch-job-fixed-${job.id}`}
                      >
                        <Lock className="h-3 w-3 text-gray-300" />
                      </div>
                    ) : (
                      /* Route job — up/down arrows with blocked-state awareness */
                      <>
                        <button
                          type="button"
                          onClick={() => onMoveJob(job.id, 'up')}
                          disabled={idx === 0 || isBlockedUp}
                          className={`w-4 h-4 flex items-center justify-center rounded transition-colors ${
                            idx === 0
                              ? 'opacity-0 pointer-events-none'
                              : isBlockedUp
                                ? 'text-gray-200 cursor-not-allowed'
                                : 'text-gray-300 hover:text-gray-600 hover:bg-gray-100'
                          }`}
                          data-testid={`dispatch-job-move-up-${job.id}`}
                          title={isBlockedUp ? 'Blocked by fixed appointment' : 'Move up'}
                          aria-label="Move up"
                        >
                          <ChevronUp className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => onMoveJob(job.id, 'down')}
                          disabled={idx === jobs.length - 1 || isBlockedDown}
                          className={`w-4 h-4 flex items-center justify-center rounded transition-colors ${
                            idx === jobs.length - 1
                              ? 'opacity-0 pointer-events-none'
                              : isBlockedDown
                                ? 'text-gray-200 cursor-not-allowed'
                                : 'text-gray-300 hover:text-gray-600 hover:bg-gray-100'
                          }`}
                          data-testid={`dispatch-job-move-down-${job.id}`}
                          title={isBlockedDown ? 'Blocked by fixed appointment' : 'Move down'}
                          aria-label="Move down"
                        >
                          <ChevronDown className="h-3 w-3" />
                        </button>
                      </>
                    )}
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <DispatchJobCard
                    task={job}
                    lead={leadsMap[job.leadId]}
                    canDrag={canDrag && !isAppointment}
                    canChangeStatus={canChangeStatus}
                    isSelected={selectedJobIds?.has(job.id)}
                    onToggleSelect={onToggleSelect}
                  />
                </div>
              </div>
            );
          })}
        </SortableContext>

        {jobs.length === 0 && !isOver && (
          <p className="text-[11px] text-gray-400 text-center py-4">
            No jobs assigned
          </p>
        )}
      </div>
    </div>
  );
};

export default DispatchTechColumn;
